import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ToastContainer, toast } from "react-toastify";
import { Helmet } from "react-helmet";
import ScrollToTop from "../../../components/whatsapp connect";
import LoaderMain from "../../../layout/header/loader_sub";
import StarIcon from "@mui/icons-material/Star";
import { Rating } from "@mui/material";
import { backend, producturl } from "../../../../path";

function TopSellingPage(props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [deals, setDeals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const [authenticated, setAuthenticated] = useState(
    JSON.parse(localStorage.getItem("userdata"))
  );

  useEffect(() => {
    fetchDeals();
  }, [currentPage]);

  const fetchDeals = () => {
    setLoading(true);
    setError(null);

    fetch(backend + "getallspecialproduts", { method: "GET" })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.json();
      })
      .then((res) => {
        setData(res);
        const list = res?.getTopSelling || [];
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        setDeals(list.slice(startIndex, endIndex));
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleProductRoute = (slug) => {
    navigate(`/product/${slug}`);
  };

  const handleAddCart = async (id) => {
    if (authenticated === null) {
      if (typeof window.openAuthModal === "function") {
        window.openAuthModal("login");
      } else {
        navigate("/login");
      }
      return;
    }
    const formData = { status: 1, quantity: 1, product_id: id };
    try {
      const response = await fetch(backend + "addtocartuserside", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authenticated?.access_token}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.status === 200) {
        toast.success("Item added to cart", { position: "top-right", autoClose: 3000 });
        props.setCartChange && props.setCartChange(!props.cartChange);
      } else if (response.status === 401) {
        toast.error("Item already exits in the cart", { position: "top-right", autoClose: 3000 });
      } else {
        toast.error("Something went wrong. Please try again later.", { position: "top-right", autoClose: 3000 });
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again later.", { position: "top-right", autoClose: 3000 });
    }
  };

  const handleAddToWishList = async (id) => {
    if (authenticated === null) {
      if (typeof window.openAuthModal === "function") {
        window.openAuthModal("login");
      } else {
        navigate("/login");
      }
      return;
    }
    const formData = { status: 0, product_id: id };
    try {
      const response = await fetch(backend + "addtowishlistuserside", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authenticated?.access_token}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.status === 200) {
        toast.success("Item added to wishlist", { position: "top-right", autoClose: 3000 });
        props.setWishChange && props.setWishChange(!props.wishChange);
      } else if (response.status === 401) {
        toast.error("Item already exits in the wishlist", { position: "top-right", autoClose: 3000 });
      }
    } catch (e) {
      // silently ignore
    }
  };

  const topSellingList = data?.getTopSelling || [];
  const totalTopSelling = topSellingList.length;
  const vendorCount = new Set(
    topSellingList.map((item) => item?.vendor_name).filter(Boolean)
  ).size;
  const averageRatingValue = topSellingList.reduce((sum, item) => {
    const rating = Number(item?.average_stars);
    return sum + (Number.isFinite(rating) ? rating : 0);
  }, 0);
  const averageRating = totalTopSelling
    ? (averageRatingValue / totalTopSelling).toFixed(1)
    : "0.0";

  return (
    <>
      <ToastContainer />
      <Helmet>
        <title> Top Selling Products </title>
        <meta name="description" content="Discover today's special deals" />
      </Helmet>
      <TopSellingMain>
        <PageWrapper>
          {/* Visual Hero Banner */}
          <div 
            className="relative w-full overflow-hidden text-left mb-10 rounded-3xl flex flex-col md:flex-row items-stretch"
            style={{
              background: "linear-gradient(135deg, #8B6508 0%, #5B4003 100%)",
              border: "1px solid rgba(197, 168, 128, 0.15)"
            }}
          >
            {/* Ambient light patterns */}
            <div className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(#F2ECE1_1px,transparent_1px)] [background-size:16px_16px]" />
            <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-[#5B4003]/40 filter blur-3xl pointer-events-none" />
            
            {/* Left Content (Text) */}
            <div className="w-full md:w-[60%] relative z-10 flex flex-col justify-center py-12 px-6 sm:px-12 md:px-16 lg:py-20">
              {/* Floating breadcrumbs */}
              <div className="inline-flex self-start items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-white/10 text-xs font-medium text-white/80 mb-6 font-jost">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span className="text-[#F2ECE1]/60">/</span>
                <span className="text-[#F2ECE1]">Top Selling Products</span>
              </div>
              
              <span className="block text-[#F2ECE1] font-jost text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2">
                Best Sellers
              </span>
              <h1 className="text-white font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                Top Selling Products
              </h1>
              <div className="h-0.5 w-20 bg-[#F2ECE1] mb-6"></div>
              <p className="text-white/80 font-jost text-sm sm:text-base leading-relaxed max-w-xl text-justify mb-0">
                Stock up on the highest performing classics, handlooms, and traditional crispy snacks. Handcrafted with authentic recipes directly from master practitioners.
              </p>
            </div>

            {/* Right Column – Full-Height Filled Image Showcase */}
            <div 
              className="hidden md:block md:w-[40%] relative overflow-hidden select-none flex-shrink-0"
              style={{
                clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0% 100%)"
              }}
            >
              <img 
                src="/storage/category/topselling_plain.png" 
                alt="Top Selling Products" 
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
              {/* Inner ambient glow/overlay */}
              <div className="absolute inset-0 bg-[#5B4003]/10 pointer-events-none" />
              {/* Seamless gradient fade-out to matching theme background */}
              <div 
                className="absolute inset-y-0 left-0 w-24 pointer-events-none bg-gradient-to-r from-[#8B6508] to-transparent"
              />
            </div>
          </div>

          <ContentSection>
            <ControlsRow>
              <CollectionInfo>
                Stock up on <strong>{totalTopSelling}</strong> best-selling treats
                curated by food lovers like you.
              </CollectionInfo>
              <ControlsGroup>
                <MetaPill>
                  <span>{deals.length}</span> items on this page
                </MetaPill>
              </ControlsGroup>
            </ControlsRow>

            {loading ? (
              <LoadingState>
                <LoaderMain />
              </LoadingState>
            ) : error ? (
              <ErrorState>
                <ErrorTitle>We couldn&apos;t load the top sellers.</ErrorTitle>
                <ErrorMessage>{error.message}</ErrorMessage>
              </ErrorState>
            ) : totalTopSelling === 0 ? (
              <EmptyState>
                <EmptyBadge>Top sellers</EmptyBadge>
                <EmptyTitle>No favourites yet</EmptyTitle>
                <EmptySubtitle>
                  Fresh arrivals are on the way. Explore other categories to find
                  your next favourite.
                </EmptySubtitle>
                <EmptyActions>
                  <PrimaryButton onClick={() => navigate("/")}>
                    Discover products
                  </PrimaryButton>
                </EmptyActions>
              </EmptyState>
            ) : (
              <>
                <ProductGrid>
                  {deals.map((item) => {
                    const salePrice = Number(item?.sale_price) || 0;
                    const mrp = Number(item?.mrp) || 0;
                    const discount = mrp && mrp > salePrice
                      ? Math.round(((mrp - salePrice) / mrp) * 100)
                      : 0;

                    return (
                      <ProductCard key={item.product_id || item.product_slug}>
                        <CardMedia>
                          <ProductImage
                            onClick={() => handleProductRoute(item.product_slug)}
                            src={
                              item.thumbnail2
                                ? producturl + item.thumbnail2
                                : producturl + item.thumbnail
                            }
                            alt={item?.product_name ?? "Top selling product"}
                          />
                          <CardOverlay>
                            <RatingChip>
                              <span>BESTSELLER</span>
                            </RatingChip>
                            {discount > 0 && (
                              <DiscountChip>-{discount}%</DiscountChip>
                            )}
                            <ActionButton
                              aria-label="Save to wishlist"
                              onClick={() => handleAddToWishList(item.product_id)}
                            >
                              <i className="fi-rs-heart"></i>
                            </ActionButton>
                          </CardOverlay>
                        </CardMedia>

                        <ProductBottom>
                          <ProductCategory>
                            {item?.vendor_name ?? "Kerala Special"}
                          </ProductCategory>
                          <ProductName
                            onClick={() => handleProductRoute(item.product_slug)}
                            title={item?.product_name}
                          >
                            {item?.product_name}
                          </ProductName>
                          <RatingRow>
                            <Rating
                              name={`topselling-rating-${item.product_id}`}
                              value={Number(item?.average_stars) || 0}
                              size="small"
                              precision={0.5}
                              readOnly
                            />
                            <RatingText>
                              ({item?.average_stars ?? "Yet to rate"})
                            </RatingText>
                          </RatingRow>
                          <CardFooter>
                            <PriceBlock>
                              <SalePrice>
                                ₹ {salePrice.toLocaleString("en-IN")}
                              </SalePrice>
                              {mrp > salePrice && (
                                <OriginalPrice>
                                  ₹ {mrp.toLocaleString("en-IN")}
                                </OriginalPrice>
                              )}
                            </PriceBlock>
                            <PrimaryButton
                              aria-label="Add to cart"
                              onClick={() => handleAddCart(item.product_id)}
                            >
                              <i className="fi-rs-shopping-cart"></i>
                              <span>Add</span>
                            </PrimaryButton>
                          </CardFooter>
                        </ProductBottom>
                      </ProductCard>
                    );
                  })}
                </ProductGrid>

                <PaginationBar>
                  <PaginationButton
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </PaginationButton>
                  <PaginationMeta>Page {currentPage}</PaginationMeta>
                  <PaginationButton
                    type="button"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={deals.length < productsPerPage}
                  >
                    Next
                  </PaginationButton>
                </PaginationBar>
              </>
            )}
          </ContentSection>
          <ScrollToTop />
        </PageWrapper>
      </TopSellingMain>
    </>
  );
}

export default TopSellingPage;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const TopSellingMain = styled.main`
  background: #f9fafb;
  min-height: 100vh;
  padding-top: 140px;

  @media screen and (max-width: 1080px) {
    padding-top: 90px;
  }
`;

const PageWrapper = styled.div`
  max-width: 90vw;
  margin: 0 auto;
  padding: 40px 24px 120px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media screen and (max-width: 768px) {
    padding: 28px 18px 80px;
  }
`;

const HeroSection = styled.section`
  background: white;
  border-radius: 24px;
  padding: 32px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  animation: ${fadeInScale} 0.6s ease forwards;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    gap: 24px;
  }
`;

const BreadcrumbTrail = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;

  a {
    color: inherit;
    font-weight: 500;
  }

  span:last-child {
    color: #111827;
    font-weight: 600;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fadeInUp} 0.7s ease forwards;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.5px;
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 15px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  @media screen and (max-width: 768px) {
    align-items: flex-start;
  }
`;

const StatValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
`;

const StatLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-top: 6px;
`;

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const CollectionInfo = styled.p`
  margin: 0;
  font-size: 15px;
  color: #4b5563;

  strong {
    color: #111827;
  }
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(179, 63, 17, 0.14);
  color: #912d05;
  font-weight: 600;
  font-size: 14px;
`;

const LoadingState = styled.div`
  padding: 80px 0;
  display: flex;
  justify-content: center;
`;

const ErrorState = styled.div`
  background: rgba(248, 113, 113, 0.1);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  color: #b91c1c;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ErrorTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  margin: 0;
  font-size: 14px;
`;

const EmptyState = styled.div`
  background: #ffffff;
  border-radius: 28px;
  padding: 64px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
  box-shadow: 0 32px 78px rgba(15, 23, 42, 0.08);
`;

const EmptyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(179, 63, 17, 0.16);
  color: #912d05;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.4px;
`;

const EmptyTitle = styled.h2`
  margin: 0;
  font-size: 30px;
  color: #111827;
`;

const EmptySubtitle = styled.p`
  margin: 0;
  color: #4b5563;
  max-width: 440px;
  line-height: 1.6;
`;

const EmptyActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  
  @media screen and (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.article`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(139, 101, 8, 0.1);
  border-top: 3px solid #8B6508;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 25px rgba(139, 101, 8, 0.12);
  }
`;

const CardMedia = styled.div`
  position: relative;
  padding-top: 90%;
  overflow: hidden;
`;

const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.35s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const RatingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #8B6508;
  color: #ffffff;
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
`;

const DiscountChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 99, 71, 0.9);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.75);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(179, 63, 17, 0.9);
  }

  i {
    font-size: 18px;
  }
`;

const ProductBottom = styled.div`
  padding: 16px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const ProductCategory = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 700;
  color: #8B6508;
`;

const ProductName = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2D1F18;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 38px;
  line-height: 1.4;
  
  &:hover {
    color: #8B6508;
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RatingText = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: auto;
`;

const PriceBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const SalePrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #2D1F18;
  font-family: 'Poppins', sans-serif;
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: #dc2626;
  text-decoration: line-through;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: #8B6508;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #5B4003;
    transform: scale(1.05);
  }
  
  span {
    display: none;
  }
`;

const PaginationBar = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const PaginationButton = styled.button`
  padding: 10px 20px;
  border-radius: 999px;
  border: none;
  background: #0f172a;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:disabled {
    background: #d1d5db;
    color: #6b7280;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #b33f11;
  }
`;

const PaginationMeta = styled.span`
  font-size: 14px;
  color: #4b5563;
`;
